import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { changePassword, getUserProfile } from '../api/userprofile';

export const useUserProfile = () => {
  const { setError } = useAuthStore();

  const {
    data: userData,
    isLoading,
    error,
    refetch
  } = useQuery('userProfile', getUserProfile, {
    onError: (error) => {
      setError(error?.response?.data?.message || 'Failed to fetch user profile');
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    userData,
    isLoading,
    error,
    refetchProfile: refetch
  };
};

export const useChangePassword = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  const validatePassword = (newPassword, confirmPassword) => {
    if (!newPassword || !confirmPassword) {
      throw new Error("Please fill in both password fields!");
    }
    if (newPassword !== confirmPassword) {
      throw new Error("Passwords don't match!");
    }
    if (newPassword.length < 6) {
      throw new Error("Password should be at least 6 characters long!");
    }
  };

  const changePasswordMutation = useMutation(changePassword, {
    onMutate: (variables) => {
      try {
        validatePassword(variables.newPassword, variables.confirmPassword);
      } catch (error) {
        toast.error(error.message);
        return Promise.reject(error);
      }
    },
    onSuccess: () => {
      toast.success("Password changed successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      queryClient.invalidateQueries('userProfile');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to change password';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    changePassword: changePasswordMutation.mutate,
    isLoading: changePasswordMutation.isLoading,
    isSuccess: changePasswordMutation.isSuccess,
    isError: changePasswordMutation.isError,
    error: changePasswordMutation.error,
    reset: changePasswordMutation.reset
  };
};